import { useMemo } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import type { Locale } from '@worldcoo/monolith/core';
import { getTranslatedCountry } from '@worldcoo/shared/country-translations';
import { countries } from '@worldcoo/monolith/forms/countries';
import { NativeSelect, NativeSelectOptGroup, NativeSelectOption } from '../native-select';
import { cn } from '../../lib/utils';

export type NativeCountryFieldTexts = {
	placeholder: string;
	popular: string;
	allCountries: string;
};

const priorityCountries = countries.filter((c) => c.priority === 1);
const nonPriorityCountries = countries.filter((c) => c.priority >= 2);

/**
 * A native `CountryField`: a combobox's `<button>` is invisible to autofill. Values are ISO 3166-1
 * alpha-2, what the `country` token delivers. Costs type-to-search over ~200 options.
 */
export default function NativeCountryField({
	label,
	locale = 'en',
	className,
	texts,
	optional,
	autoComplete = 'country',
}: {
	label: string;
	locale?: Locale;
	className?: string;
	texts: NativeCountryFieldTexts;
	optional?: boolean;
	autoComplete?: string;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);

	const priorityItems = useMemo(
		() =>
			priorityCountries.map((c) => ({
				value: c.country,
				label: getTranslatedCountry(c.country, locale) ?? c.name,
			})),
		[locale],
	);
	const otherItems = useMemo(
		() =>
			nonPriorityCountries
				.map((c) => ({ value: c.country, label: getTranslatedCountry(c.country, locale) ?? c.name }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[locale],
	);

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<NativeSelect
				className="w-full"
				id={field.name}
				name={field.name}
				autoComplete={autoComplete}
				aria-invalid={hasFieldError(field.state.meta)}
				disabled={formReadOnly}
				value={field.state.value ?? ''}
				onBlur={field.handleBlur}
				onChange={(event) => field.handleChange(event.target.value)}
			>
				<NativeSelectOption value="">{texts.placeholder}</NativeSelectOption>
				<NativeSelectOptGroup label={texts.popular}>
					{priorityItems.map((item) => (
						<NativeSelectOption key={item.value} value={item.value}>
							{item.label}
						</NativeSelectOption>
					))}
				</NativeSelectOptGroup>
				<NativeSelectOptGroup label={texts.allCountries}>
					{otherItems.map((item) => (
						<NativeSelectOption key={item.value} value={item.value}>
							{item.label}
						</NativeSelectOption>
					))}
				</NativeSelectOptGroup>
			</NativeSelect>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
